import { useAuthContext } from '../contexts/auth-context';

/**
 * 🔴 CONTRATO consumido pelo `AppRouter` — implementação real ligada na **T1.9**.
 *
 * Existe desde o M0 porque o roteamento por plataforma é decisão **estrutural**: em nativo não há
 * landing nem `/admin`, e isso precisa estar travado por teste antes de qualquer tela existir
 * (PRD §4.6.6 · App Store Guideline 3.1.3(e)).
 *
 * A T1.9 substituiu o **corpo** — antes devolvia visitante fixo, agora lê a sessão do Supabase. A
 * **forma** `{ isAuthenticated, role }` permanece intacta de propósito: é o que o `AppRouter`
 * consome, e mudá-la quebraria o roteamento junto. `app-router.test.tsx` corre sem alteração nesta
 * fatia justamente como prova disso.
 *
 * Este hook escolhe **shell**; quem bloqueia acesso é o `session-guard`, que observa `isLoading` e
 * não decide enquanto a sessão carrega.
 */

/** Espelha o ENUM `user_role` do banco (`specs.md` §4.4). */
export type UserRole =
  | 'consumer'
  | 'professional_owner'
  | 'professional_employee'
  | 'customer_success'
  | 'admin'
  | 'system';

export type AuthState = {
  isAuthenticated: boolean;
  role: UserRole | null;
  /** Impede o roteador de decidir antes de a sessão persistida ser resolvida. */
  isLoading: boolean;
  /** Há sessão, mas a confirmação ainda não concede capacidade de produto. Só existe nesse estado. */
  requiresEmailVerification?: true;
};

export function useAuth(): AuthState {
  const { claims, session, user, isLoading } = useAuthContext();

  if (isLoading) return { isAuthenticated: false, role: null, isLoading: true };

  // Uma sessão pendente não é anónima: precisa chegar à tela `/verify`, mas continua sem shell
  // nem capacidade de produto. A RLS permanece a fronteira de dados.
  if (session && !user?.email_confirmed_at) {
    return { isAuthenticated: false, role: null, isLoading: false, requiresEmailVerification: true };
  }

  // `isInteractive` já exclui `system` e qualquer estado que não seja `active` (parser da T1.8):
  // uma conta suspensa tem sessão válida no GoTrue, mas não interface.
  if (!claims.isInteractive || claims.role === null) {
    return { isAuthenticated: false, role: null, isLoading: false };
  }
  return { isAuthenticated: true, role: claims.role, isLoading: false };
}

/** Papéis que entram no shell profissional (`/dashboard/*`). */
export const PROFESSIONAL_ROLES: readonly UserRole[] = ['professional_owner', 'professional_employee'];

export const isProfessional = (role: UserRole | null): boolean =>
  role !== null && PROFESSIONAL_ROLES.includes(role);
